import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { CheckCircle, Clock, Calendar, Users, ArrowRight } from 'lucide-react';

const IrishDental = () => {
  const examFocus = [
    "Clinical technique and precision",
    "Patient safety protocols",
    "Infection control measures",
    "Professional communication",
    "Treatment planning",
    "Evidence-based practice",
    "Clinical judgment",
    "Ethical considerations"
  ];

  const clinicalSkills = [
    "Restorative dentistry procedures",
    "Endodontic treatment techniques",
    "Periodontal assessment and treatment",
    "Oral surgery skills",
    "Prosthodontic procedures",
    "Diagnostic radiography",
    "Local anesthesia administration",
    "Emergency management"
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-[#1e3a8a] to-[#2563eb] text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              Irish Dental Council Course
            </h1>
            <p className="text-lg md:text-xl text-gray-100 mb-8">
              Specialized training for the Irish Dental Council licensing examination with focus on European dental standards
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Button asChild size="lg" variant="secondary" className="bg-white text-[#1e3a8a] hover:bg-gray-100">
                <Link to="/contact">Enroll Now</Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="border-white text-white hover:bg-white/10">
                <Link to="/courses">View All Courses</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Course Details */}
      <section className="py-12 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-6">
            <Card>
              <CardContent className="pt-6">
                <Clock className="h-8 w-8 text-[#1e3a8a] mb-3" />
                <div className="text-2xl font-bold text-gray-900 mb-1">4-5 Weeks</div>
                <div className="text-sm text-gray-600">Duration</div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <Calendar className="h-8 w-8 text-[#1e3a8a] mb-3" />
                <div className="text-2xl font-bold text-gray-900 mb-1">Mon-Fri</div>
                <div className="text-sm text-gray-600">10 AM - 7 PM</div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <Calendar className="h-8 w-8 text-[#1e3a8a] mb-3" />
                <div className="text-2xl font-bold text-gray-900 mb-1">Saturday</div>
                <div className="text-sm text-gray-600">10 AM - 2 PM</div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <Users className="h-8 w-8 text-[#1e3a8a] mb-3" />
                <div className="text-2xl font-bold text-gray-900 mb-1">Full-time</div>
                <div className="text-sm text-gray-600">Intensive Training</div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* About Irish Dental Council */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Irish Dental Council Examination
              </h2>
              <p className="text-lg text-gray-600 mb-6">
                The Irish Dental Council requires international dental graduates to pass a comprehensive examination to practice dentistry in Ireland. The exam assesses clinical competence, patient safety awareness, and adherence to European dental standards.
              </p>
              <p className="text-gray-600 mb-6">
                Our training program prepares you thoroughly for both the clinical and practical components of the examination, with emphasis on technique precision and patient-centered care.
              </p>
              <Button asChild className="bg-[#1e3a8a] hover:bg-[#1e3a8a]/90">
                <Link to="/contact">Get Started Today <ArrowRight className="ml-2 h-4 w-4" /></Link>
              </Button>
            </div>
            <div className="bg-[#1e3a8a]/5 rounded-2xl p-8 border-2 border-[#1e3a8a]/20">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Examination Focus Areas</h3>
              <div className="space-y-3">
                {examFocus.map((item, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <CheckCircle className="h-5 w-5 text-[#1e3a8a] flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Clinical Skills Training */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Clinical Skills Training
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Comprehensive hands-on training across all clinical domains
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {clinicalSkills.map((skill, index) => (
              <div key={index} className="bg-white p-6 rounded-lg shadow-sm hover:shadow-md transition-shadow">
                <CheckCircle className="h-8 w-8 text-[#1e3a8a] mb-3" />
                <p className="text-gray-700 font-medium">{skill}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why ODA for Irish Dental Council */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Why Choose ODA for Irish Dental Council Preparation
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white p-8 rounded-lg shadow-sm border-2 border-gray-100">
              <div className="text-4xl font-bold text-[#1e3a8a] mb-4">01</div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">
                European Standards
              </h3>
              <p className="text-gray-600">
                Training aligned with European dental standards and Irish regulatory requirements
              </p>
            </div>

            <div className="bg-white p-8 rounded-lg shadow-sm border-2 border-gray-100">
              <div className="text-4xl font-bold text-[#1e3a8a] mb-4">02</div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">
                Technique Precision
              </h3>
              <p className="text-gray-600">
                Focus on clinical precision and technique refinement through continuous practice
              </p>
            </div>

            <div className="bg-white p-8 rounded-lg shadow-sm border-2 border-gray-100">
              <div className="text-4xl font-bold text-[#1e3a8a] mb-4">03</div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">
                Patient Safety Focus
              </h3>
              <p className="text-gray-600">
                Emphasis on patient safety protocols and professional communication skills
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Key Features */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12">
            <div className="bg-white p-8 rounded-lg shadow-sm">
              <h3 className="text-2xl font-bold text-[#1e3a8a] mb-6">What You'll Gain</h3>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-[#1e3a8a] flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">Confidence in clinical procedures and decision-making</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-[#1e3a8a] flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">Understanding of European dental practice standards</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-[#1e3a8a] flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">Proficiency in all required clinical procedures</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-[#1e3a8a] flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">Exam strategies and time management skills</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-[#1e3a8a] flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">Continuous feedback and performance tracking</span>
                </li>
              </ul>
            </div>

            <div className="bg-[#1e3a8a] text-white p-8 rounded-lg shadow-sm">
              <h3 className="text-2xl font-bold mb-6">Training Support</h3>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 flex-shrink-0 mt-0.5" />
                  <span>Expert faculty with international experience</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 flex-shrink-0 mt-0.5" />
                  <span>All equipment and materials provided</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 flex-shrink-0 mt-0.5" />
                  <span>Mock examinations in realistic settings</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 flex-shrink-0 mt-0.5" />
                  <span>Personalized learning plans</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 flex-shrink-0 mt-0.5" />
                  <span>Post-training support and guidance</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-[#1e3a8a]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Start Your Irish Dental Council Preparation
          </h2>
          <p className="text-lg text-gray-200 mb-8 max-w-2xl mx-auto">
            Take the first step towards practicing dentistry in Ireland
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button asChild size="lg" variant="secondary" className="bg-white text-[#1e3a8a] hover:bg-gray-100">
              <Link to="/contact">Enroll Now <ArrowRight className="ml-2 h-4 w-4" /></Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="border-white text-white hover:bg-white/10">
              <Link to="/courses">View All Courses</Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default IrishDental;
